import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useProject } from './ProjectContext';
import { useSubscription } from './SubscriptionContext';
import {
  taskSignatures,
  serializeSignatures,
  deserializeSignatures,
  detectActionSignals,
  ONB_ACTION_EVENT,
  type ChecklistKey,
  type TaskSig,
} from '../utils/onboardingSignals';
import { resetOnboarding } from '../components/common/OnboardingTour';
import analytics from '../services/analyticsService';

const CHECKLIST_KEY = 'meugantt_onboarding_v2_checklist';
const BASE_PREFIX = 'meugantt_onb_base_';
const CP_VIEWED_KEY = 'meugantt_onb_cp_viewed';

/**
 * Grupos de processos do PMBoK: o checklist ensina o método, não só a ferramenta.
 * A ordem aqui é a ordem de exibição.
 */
export const CHECKLIST_GROUPS = ['Planejamento', 'Execução', 'Monitoramento e Controle'] as const;
export type ChecklistGroup = (typeof CHECKLIST_GROUPS)[number];

// O rótulo saiu daqui: vem do idioma, indexado pela ChecklistKey. A chave e o
// grupo continuam sendo estrutura (persistem em localStorage e casam com o
// evento que os módulos emitem).
const ITEMS: Array<{ key: ChecklistKey; group: ChecklistGroup; feature?: string }> = [
  // Planejamento
  { key: 'create_project', group: 'Planejamento' },
  { key: 'edit_task', group: 'Planejamento' },
  { key: 'add_subtask', group: 'Planejamento' },
  { key: 'add_dependency', group: 'Planejamento' },
  { key: 'add_member', group: 'Planejamento' },
  // Execução
  { key: 'assign_owner', group: 'Execução' },
  { key: 'move_kanban', group: 'Execução' },
  { key: 'create_pendencia', group: 'Execução', feature: 'todos' },
  { key: 'set_raci', group: 'Execução', feature: 'raci_matrix' },
  // Monitoramento e Controle
  { key: 'save_baseline', group: 'Monitoramento e Controle', feature: 'schedule_baseline' },
  { key: 'view_critical_path', group: 'Monitoramento e Controle', feature: 'critical_path' },
  { key: 'create_risk', group: 'Monitoramento e Controle', feature: 'risks' },
  { key: 'create_change', group: 'Monitoramento e Controle', feature: 'changes' },
  { key: 'create_cost', group: 'Monitoramento e Controle', feature: 'costs' },
  { key: 'view_portfolio', group: 'Monitoramento e Controle', feature: 'portfolio' },
];

const VALID_KEYS = new Set<ChecklistKey>(ITEMS.map((i) => i.key));

export interface ChecklistItem {
  key: ChecklistKey;
  group: ChecklistGroup;
  done: boolean;
  locked: boolean;
}

export interface ChecklistSection {
  group: ChecklistGroup;
  items: ChecklistItem[];
  doneCount: number;
  totalCount: number;
}

interface PersistState {
  done: ChecklistKey[];
  dismissed: boolean;
  minimized: boolean;
}

interface OnboardingContextValue {
  items: ChecklistItem[];
  sections: ChecklistSection[];
  doneCount: number;
  totalCount: number;
  progress: number;
  allDone: boolean;
  dismissed: boolean;
  minimized: boolean;
  setMinimized: (v: boolean) => void;
  dismiss: () => void;
  restartTour: () => void;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

function loadPersist(): PersistState {
  try {
    const raw = localStorage.getItem(CHECKLIST_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PersistState>;
      return {
        done: Array.isArray(parsed.done) ? (parsed.done as ChecklistKey[]) : [],
        dismissed: !!parsed.dismissed,
        minimized: parsed.minimized !== false, // default minimizado (launcher discreto)
      };
    }
  } catch { /* ignore */ }
  // Estado de repouso: launcher minimizado no canto, não um painel ocupando a tela.
  return { done: [], dismissed: false, minimized: true };
}

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const { tasks, projects, currentProjectId, hasBaseline } = useProject();
  const { hasFeature, isSystemAdmin } = useSubscription();

  const [persist, setPersist] = useState<PersistState>(loadPersist);
  const persistRef = useRef(persist);
  persistRef.current = persist;

  const writePersist = useCallback((next: PersistState) => {
    persistRef.current = next;
    setPersist(next);
    try { localStorage.setItem(CHECKLIST_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  }, []);

  // Chaves que o plano do usuário realmente libera — base do "checklist concluído".
  // Itens bloqueados não podem ser feitos e não devem travar a conclusão.
  const unlockedKeysRef = useRef<ChecklistKey[]>([]);

  const markDone = useCallback((key: ChecklistKey) => {
    const cur = persistRef.current;
    if (cur.done.includes(key)) return;
    const done = [...cur.done, key];
    writePersist({ ...cur, done });

    analytics.trackChecklistItemDone(key);

    // Conclusão dispara na transição (markDone só entra aqui uma vez por chave).
    const unlocked = unlockedKeysRef.current;
    if (unlocked.length > 0 && unlocked.every((k) => done.includes(k))) {
      analytics.trackChecklistComplete(done.length);
    }
  }, [writePersist]);

  // Baseline (snapshot do estado semeado) por projeto, para detectar ações do usuário por diff.
  const baseRef = useRef<Map<string, TaskSig> | null>(null);
  const baseProjectRef = useRef<string | null>(null);

  useEffect(() => {
    if (!currentProjectId || tasks.length === 0) return;
    if (baseProjectRef.current === currentProjectId && baseRef.current) return;
    baseProjectRef.current = currentProjectId;
    const storageKey = BASE_PREFIX + currentProjectId;
    const stored = (() => { try { return localStorage.getItem(storageKey); } catch { return null; } })();
    if (stored) {
      baseRef.current = deserializeSignatures(stored);
    }
    if (!baseRef.current) {
      const sig = taskSignatures(tasks);
      baseRef.current = sig;
      try { localStorage.setItem(storageKey, serializeSignatures(sig)); } catch { /* ignore */ }
    }
  }, [currentProjectId, tasks]);

  // Recalcular marcos a cada mudança de estado vivo.
  useEffect(() => {
    if (projects.length > 0) markDone('create_project');
    if (hasBaseline()) markDone('save_baseline');
    try { if (localStorage.getItem(CP_VIEWED_KEY) === '1') markDone('view_critical_path'); } catch { /* ignore */ }

    const base = baseRef.current;
    if (base && tasks.length > 0) {
      const signals = detectActionSignals(base, taskSignatures(tasks));
      (Object.keys(signals) as ChecklistKey[]).forEach((k) => { if (signals[k]) markDone(k); });
    }
  }, [tasks, projects.length, hasBaseline, markDone]);

  // Caminho crítico é estado local do Gantt: o GanttModule emite um evento ao ativar.
  useEffect(() => {
    const onCp = () => {
      try { localStorage.setItem(CP_VIEWED_KEY, '1'); } catch { /* ignore */ }
      markDone('view_critical_path');
    };
    window.addEventListener('meugantt:cp-viewed', onCp);
    return () => window.removeEventListener('meugantt:cp-viewed', onCp);
  }, [markDone]);

  // Ações registradas pelos módulos (risco criado, custo lançado, RACI definido...).
  // Cada módulo emite o evento ao lado do seu analytics; nenhum importa este contexto.
  useEffect(() => {
    const onAction = (e: Event) => {
      const key = (e as CustomEvent<{ key?: ChecklistKey }>).detail?.key;
      if (key && VALID_KEYS.has(key)) markDone(key);
    };
    window.addEventListener(ONB_ACTION_EVENT, onAction);
    return () => window.removeEventListener(ONB_ACTION_EVENT, onAction);
  }, [markDone]);

  const items: ChecklistItem[] = useMemo(() => {
    const doneSet = new Set(persist.done);
    return ITEMS.map((it) => ({
      key: it.key,
      group: it.group,
      done: doneSet.has(it.key),
      locked: !!it.feature && !isSystemAdmin && !hasFeature(it.feature),
    }));
  }, [persist.done, isSystemAdmin, hasFeature]);

  useEffect(() => {
    unlockedKeysRef.current = items.filter((i) => !i.locked).map((i) => i.key);
  }, [items]);

  const sections: ChecklistSection[] = useMemo(() => (
    CHECKLIST_GROUPS
      .map((group) => {
        const groupItems = items.filter((i) => i.group === group);
        // Itens bloqueados continuam visíveis (servem de upsell), mas ficam fora
        // da contagem: cobrar do usuário Free um passo que ele não pode dar
        // transforma o checklist em placar de derrota.
        const achievable = groupItems.filter((i) => !i.locked);
        return {
          group,
          items: groupItems,
          doneCount: achievable.filter((i) => i.done).length,
          totalCount: achievable.length,
        };
      })
      .filter((s) => s.items.length > 0)
  ), [items]);

  const achievable = items.filter((i) => !i.locked);
  const doneCount = achievable.filter((i) => i.done).length;
  const totalCount = achievable.length;
  const progress = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;
  const allDone = totalCount > 0 && doneCount === totalCount;

  const setMinimized = useCallback((v: boolean) => {
    writePersist({ ...persistRef.current, minimized: v });
  }, [writePersist]);

  const dismiss = useCallback(() => {
    writePersist({ ...persistRef.current, dismissed: true });
  }, [writePersist]);

  const restartTour = useCallback(() => {
    resetOnboarding();
    writePersist({ ...persistRef.current, dismissed: false, minimized: false });
    (window as unknown as { __startOnboarding?: () => void }).__startOnboarding?.();
  }, [writePersist]);

  const value: OnboardingContextValue = {
    items,
    sections,
    doneCount,
    totalCount,
    progress,
    allDone,
    dismissed: persist.dismissed,
    minimized: persist.minimized,
    setMinimized,
    dismiss,
    restartTour,
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding deve ser usado dentro de OnboardingProvider');
  return ctx;
}
